import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import { Section, Button, Input, Select, Badge, Empty } from "@/components/ui/kit";
import { Plus, Building2, User } from "lucide-react";

export default function DonorsList() {
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [type, setType] = useState("");
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/donors", { params: { page, size: 20, search: search || undefined, type: type || undefined } })
      .then(r => { setRows(r.data.data.content); setTotalPages(r.data.data.totalPages || 1); })
      .finally(() => setLoading(false));
  };

  useEffect(load, [page, type]);

  return (
    <div className="space-y-6">
      <Section
        title="Donors"
        kicker="Rajalakshmi Children Foundation, Belagavi"
        right={<div className="flex gap-2">
          <Link to="/admin/donors/new/individual"><Button variant="secondary" data-testid="add-individual-donor-btn"><User className="w-4 h-4"/> Add Ind Donor</Button></Link>
          <Link to="/admin/donors/new/corporate"><Button data-testid="add-corporate-donor-btn"><Building2 className="w-4 h-4"/> Add Corp Donor</Button></Link>
        </div>}
      />

      <div className="rounded-xl border border-emerald-900/10 bg-white p-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Input testid="donor-search" placeholder="Search by name, code, email…"
            value={search} onChange={e=>setSearch(e.target.value)}
            onKeyDown={e => e.key === "Enter" && (setPage(0), load())} />
          <Select testid="donor-type-filter" value={type} onChange={e=>{setType(e.target.value); setPage(0);}}>
            <option value="">All types</option>
            <option value="INDIVIDUAL">Individual</option>
            <option value="CORPORATE">Corporate</option>
          </Select>
          <Button variant="secondary" data-testid="donor-search-btn" onClick={()=>{setPage(0); load();}}>Search</Button>
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-widest text-emerald-800/70 border-b border-emerald-900/10">
                <th className="py-2 pr-4">Code</th>
                <th className="py-2 pr-4">Name</th>
                <th className="py-2 pr-4">Type</th>
                <th className="py-2 pr-4">Email</th>
                <th className="py-2 pr-4">Phone</th>
                <th className="py-2 pr-4">Location</th>
                <th className="py-2 pr-4">PAN</th>
                <th className="py-2 pr-4"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.donorId} data-testid={`donor-row-${r.donorId}`} className="border-b border-emerald-900/5 hover:bg-emerald-50/40">
                  <td className="py-3 pr-4 mono text-emerald-900">{r.donorCode}</td>
                  
                  {/* Updated to dynamically include the Salutation if available */}
                  <td className="py-3 pr-4 font-semibold text-emerald-950">
                    {r.donorType === "INDIVIDUAL" && r.salutation ? `${r.salutation} ` : ""}{r.name}
                  </td>
                  
                  <td className="py-3 pr-4">
                    {r.donorType === "INDIVIDUAL"
                      ? <Badge tone="brand">Individual</Badge>
                      : <Badge tone="indigo">Corporate</Badge>}
                  </td>
                  <td className="py-3 pr-4 text-muted-foreground">{r.email}</td>
                  <td className="py-3 pr-4 mono text-xs">{r.primaryContactNo}</td>
                  <td className="py-3 pr-4 text-muted-foreground">{[r.city, r.state].filter(Boolean).join(", ")}</td>
                  <td className="py-3 pr-4 mono text-xs">{r.panNumber || "—"}</td>
                  <td className="py-3 pr-4">
                    <Link to={`/admin/donors/${r.donorId}`} data-testid={`view-donor-${r.donorId}`} className="text-emerald-800 font-semibold text-xs hover:underline">View</Link>
                  </td>
                </tr>
              ))}
              {!loading && !rows.length && (
                <tr><td colSpan={8}><Empty title="No donors found" note="Add an Individual or Corporate donor to get started." testid="donors-empty" /></td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex items-center justify-between text-xs">
          <div className="text-muted-foreground">Page {page + 1} of {Math.max(1, totalPages)}</div>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" data-testid="donors-prev" disabled={page===0} onClick={()=>setPage(p=>p-1)}>Prev</Button>
            <Button variant="secondary" size="sm" data-testid="donors-next" disabled={page+1>=totalPages} onClick={()=>setPage(p=>p+1)}>Next</Button>
          </div>
        </div>
      </div>
    </div>
  );
}